import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import { usePagedData } from '../../hooks/usePagedData';
import { inventoryApi } from '../../api/endpoints';
import { InventoryRecordDto, ChannelMap } from '../../types';
import Select from '../../components/common/Select';
import { FiRefreshCw, FiSearch, FiCheck, FiX, FiDownload } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { exportApi } from '../../api/endpoints';
import { downloadExcel } from '../../utils/downloadFile';

const InventoryPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [syncingFrom, setSyncingFrom] = useState(false);
  const [syncingTo, setSyncingTo] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<number>(0);

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<InventoryRecordDto>(inventoryApi.list, {
    initialSortBy: 'productTitle',
    initialSortDesc: false,
  });

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput);
    },
    [searchInput, setSearch]
  );

  const handleChannelFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        channel: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleBackorderFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        isBackordered: value === '' ? undefined : value === 'true',
      }));
    },
    [setFilters]
  );

  const handleSyncFromAmazon = useCallback(async () => {
    setSyncingFrom(true);
    try {
      await inventoryApi.syncFromAmazon();
      toast.success(t('inventory.syncFromStarted'));
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('inventory.syncError'));
    } finally {
      setSyncingFrom(false);
    }
  }, [refresh]);

  const handleSyncToAmazon = useCallback(async () => {
    setSyncingTo(true);
    try {
      await inventoryApi.syncToAmazon();
      toast.success(t('inventory.syncToStarted'));
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('inventory.syncError'));
    } finally {
      setSyncingTo(false);
    }
  }, [refresh]);

  const startEdit = useCallback((record: InventoryRecordDto) => {
    setEditingId(record.id);
    setEditValue(record.availableQuantity);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditValue(0);
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingId) return;
    try {
      await inventoryApi.update({ id: editingId, availableQuantity: editValue });
      toast.success(t('inventory.quantityUpdated'));
      setEditingId(null);
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('inventory.updateError'));
    }
  }, [editingId, editValue, refresh]);

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        saveEdit();
      } else if (e.key === 'Escape') {
        cancelEdit();
      }
    },
    [saveEdit, cancelEdit]
  );

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL') : '-';

  const columns = [
    {
      key: 'productTitle',
      header: t('inventory.product'),
      sortable: true,
      render: (row: InventoryRecordDto) => (
        <span className="text-truncate" title={row.productTitle}>
          {row.productTitle.length > 50
            ? row.productTitle.substring(0, 50) + '...'
            : row.productTitle}
        </span>
      ),
    },
    {
      key: 'asin',
      header: 'ASIN',
      sortable: true,
      render: (row: InventoryRecordDto) => row.asin || '-',
    },
    {
      key: 'sku',
      header: 'SKU',
      render: (row: InventoryRecordDto) => row.sku || '-',
    },
    {
      key: 'warehouseName',
      header: t('inventory.warehouse'),
      render: (row: InventoryRecordDto) => row.warehouseName || '-',
    },
    {
      key: 'availableQuantity',
      header: t('inventory.available'),
      sortable: true,
      render: (row: InventoryRecordDto) => {
        if (editingId === row.id) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <input
                type="number"
                className="form-control form-control-sm"
                style={{ width: '80px' }}
                value={editValue}
                onChange={(e) => setEditValue(Number(e.target.value))}
                onKeyDown={handleEditKeyDown}
                autoFocus
                min={0}
              />
              <button
                className="btn btn-sm btn-icon btn-success"
                onClick={saveEdit}
                title={t('common.save')}
              >
                <FiCheck />
              </button>
              <button
                className="btn btn-sm btn-icon btn-secondary"
                onClick={cancelEdit}
                title={t('common.cancel')}
              >
                <FiX />
              </button>
            </div>
          );
        }
        return (
          <span
            className="editable-cell"
            onClick={() => startEdit(row)}
            title={t('inventory.clickToEdit')}
            style={{ cursor: 'pointer', borderBottom: '1px dashed #999' }}
          >
            {row.availableQuantity}
          </span>
        );
      },
    },
    {
      key: 'reservedQuantity',
      header: t('inventory.reserved'),
      render: (row: InventoryRecordDto) => row.reservedQuantity ?? '-',
    },
    {
      key: 'inboundQuantity',
      header: t('inventory.inbound'),
      render: (row: InventoryRecordDto) => row.inboundQuantity ?? '-',
    },
    {
      key: 'isBackordered',
      header: t('inventory.backorder'),
      render: (row: InventoryRecordDto) =>
        row.isBackordered ? (
          <span className="badge badge-danger">{t('common.yes')}</span>
        ) : (
          <span className="badge badge-success">{t('common.no')}</span>
        ),
    },
    {
      key: 'syncedAt',
      header: t('inventory.syncDate'),
      sortable: true,
      render: (row: InventoryRecordDto) => formatDate(row.syncedAt),
    },
  ];

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <h1>{t('inventory.title')}</h1>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => downloadExcel(exportApi.inventoryXlsx, `stany_mag_${new Date().toISOString().slice(0,10)}.xlsx`)}
          >
            <FiDownload /> Excel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSyncFromAmazon}
            disabled={syncingFrom}
          >
            <FiRefreshCw className={syncingFrom ? 'spin' : ''} />
            {syncingFrom ? t('inventory.syncing') : t('inventory.fetchFromAmazon')}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleSyncToAmazon}
            disabled={syncingTo}
          >
            <FiRefreshCw className={syncingTo ? 'spin' : ''} />
            {syncingTo ? t('inventory.syncing') : t('inventory.sendToAmazon')}
          </button>
        </div>
      </div>

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder={t('inventory.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              <FiSearch />
            </button>
          </div>
        </form>

        <div className="toolbar-filters">
          <Select
            options={[
              { value: '', label: t('products.allChannels') },
              ...Object.entries(ChannelMap).map(([key, label]) => ({ value: key, label })),
            ]}
            value={String(filters.channel ?? '')}
            onChange={handleChannelFilter}
            placeholder={t('products.allChannels')}
          />

          <Select
            options={[
              { value: '', label: t('inventory.backorderAll') },
              { value: 'true', label: t('inventory.backorderOnly') },
              { value: 'false', label: t('inventory.backorderNone') },
            ]}
            value={String(filters.isBackordered ?? '')}
            onChange={handleBackorderFilter}
            placeholder={t('inventory.backorderAll')}
          />
        </div>
      </div>

      {loading ? (
        <div className="loader-wrapper">
          <div className="loader" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data?.items || []}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
        />
      )}
    </div>
  );
};

export default InventoryPage;
